import { createSlice } from '@reduxjs/toolkit';

import type { RootState } from 'features/app/store/store';

export interface ForkingQualityDegradedPayload {
  reason: string;
  peerId: string;
  videoBitrate?: number;
  videoJitter?: number;
  videoFramesPerSecond?: number;
  videoRoundTripTime?: number;
}

export interface TrainingForkerState {
  enabled: boolean;
  active: boolean;
  wasActiveDuringCall: boolean;
  qualityDegraded: boolean;
  qualityDegradedReason: string | null;
}

// Disable training forker by default when running under a test environment.
//  - Vitest/Node does not provide WebRTC APIs (RTCPeerConnection, etc.),
//    causing unhandled promise rejections when the forker logic attempts to
//    create peer connections during integration tests.
//  - Disabling here avoids needing wide mocks and eliminates noisy errors.
//  - Individual tests that require forker behavior can explicitly dispatch
//    enableForking().
export const initialTrainingForkerState: TrainingForkerState = {
  enabled: false,
  active: false,
  wasActiveDuringCall: false,
  qualityDegraded: false,
  qualityDegradedReason: null,
};

export const trainingForkerSlice = createSlice({
  name: 'training-forker',
  initialState: initialTrainingForkerState,
  reducers: {
    enableForking: (state) => {
      state.enabled = true;
    },
    disableForking: (state) => {
      state.enabled = false;
    },

    startedForking: (state) => {
      console.log('trainingForkerSlice: startedForking called');
      state.active = true;
      state.wasActiveDuringCall = true;
    },
    stoppedForking: (state) => {
      console.log('trainingForkerSlice: stoppedForking called');
      state.active = false;
    },
    forkingQualityDegraded: (
      state,
      action: { payload: ForkingQualityDegradedPayload }
    ) => {
      if (state.qualityDegraded) {
        return;
      }

      state.qualityDegraded = true;
      state.qualityDegradedReason = action.payload.reason;
    },
    forkingQualityDegradedReset: (state) => {
      state.qualityDegraded = false;
      state.qualityDegradedReason = null;
    },
    resetTrainingForker: (state) => {
      return { ...initialTrainingForkerState, enabled: state.enabled };
    },
  },
});

export const {
  enableForking,
  disableForking,
  startedForking,
  stoppedForking,
  forkingQualityDegraded,
  forkingQualityDegradedReset,
  resetTrainingForker,
} = trainingForkerSlice.actions;

export const trainingForkerReducer = trainingForkerSlice.reducer;

export const trainingForkerSelector = ({ trainingForker }: RootState) =>
  trainingForker;

export const isTrainingForkerEnabledSelector = ({
  trainingForker,
}: RootState) => trainingForker.enabled;
